import styled from "@emotion/styled";
import { IStyledComponentProps } from "../../theme/theme.types";
import ZoomInIcon from "../../theme/assets/map-controls/zoom_in.svg";
import ZoomOutIcon from "../../theme/assets/map-controls/zoom_out.svg";
import LocationOnIcon from "../../theme/assets/map-controls/location_on.svg";
import LocationOffIcon from "../../theme/assets/map-controls/location_off.svg";
import SearchIcon from "../../theme/assets/map-controls/search.svg";
import CancelIcon from "../../theme/assets/map-controls/cancel_dark.svg";

const MapContainer = styled.div<IStyledComponentProps>`
  &.map-container {
    margin: 0;
    padding: 0;
    position: absolute;
    top: 75px;
    left: 0;
    right: 0;
    bottom: 0;
  }
`;

const BasemapGalleryContainer = styled.div<IStyledComponentProps>`
  &.basemap-gallery-container {
    margin: 0;
    padding: 0;
    position: absolute;
    top: 184px;
    right: 56px;
    height: 300px;
    @media (max-width: ${(props) =>
        props.theme.utils.getThemeBreakpoint("tablet")}) {
      height: 200px;
      right: 48px;
      top: 218px;
    }
  }
`;

const SearchContainer = styled.div<IStyledComponentProps>`
  &.search-container {
    background-color: #fff;
    width: 460px;
    margin: 20px;
    padding-left: 20px;
    height: 50px;

    .esri-icon-search {
      &:before {
        content: url(${SearchIcon});
      }
    }

    .esri-icon-close {
      &:before {
        content: url(${CancelIcon});
      }
    }

    .esri-input {
      font-family: ${(props) => props.theme?.fontFamilies?.regular};
      &:focus {
        outline: none;
        box-shadow: 0 0 5px 0 ${(props) => props.theme.colors?.brand.primary};
      }
    }

    .esri-search__suggestions-menu {
      font-family: ${(props) => props.theme?.fontFamilies?.regular};
    }

    @media (max-width: ${(props) =>
        props.theme.utils.getThemeBreakpoint("tablet")}) {
      width: 100%;
      margin: 0;
      .esri-input {
        font-size: ${(props) => props.theme.utils.getFontThemeSize("small")};
      }
    }
  }
`;

const LocationContainer = styled.div<IStyledComponentProps>`
  &.location-container {
    &.user-enabled {
      .esri-icon-locate {
        &:before {
          content: url(${LocationOnIcon});
        }
      }
    }

    &.user-disabled {
      .esri-icon-locate {
        &:before {
          content: url(${LocationOffIcon});
        }
      }
    }

    @media (max-width: ${(props) =>
        props.theme.utils.getThemeBreakpoint("tablet")}) {
      display: block !important;
      top: 60px;
      right: 10px;

      .esri-icon {
        &.esri-icon-locate {
          margin-top: ${(props) =>
            props.theme.utils.browserUtils.isMobile ? "8px" : 0};
        }
      }
    }
  }
`;

const ZoomContainer = styled.div<IStyledComponentProps>`
  &.zoom-container {
    .esri-icon-plus {
      &:before {
        content: url(${ZoomInIcon});
      }
    }
    .esri-icon-minus {
      height: 25px;
      &:before {
        content: url(${ZoomOutIcon});
      }
    }

    @media (max-width: ${(props) =>
        props.theme.utils.getThemeBreakpoint("tablet")}) {
      right: 10px;
      top: 102px;
    }

    // keep arouond in case we want to disable zooom
    /* &.disabled {
      background-color: #f3f3f3;
      .esri-widget--button {
        background-color: #f3f3f3;
        &:hover {
          background-color: #f3f3f3;
          cursor: initial;
        }

        .esri-icon-plus,
        .esri-icon-minus {
          color: #6e6e6e;
          opacity: 0.5;
        }
      }
    } */
  }
`;

const PinModeContainer = styled.div<IStyledComponentProps>`
  &.pin-mode-container {
    width: 32px;
    height: 32px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    margin: 0;
    padding: 0;
    position: absolute;
    top: 216px;
    right: 20px;

    &.in-modal {
      position: initial;
      display: inline-block;
      margin: 0 6px;
      top: 0;
      left: 0;
    }

    &.inactive {
      background-color: ${(props) => props.theme.colors?.essential.white};
      &:hover {
        background-color: ${(props) => props.theme.colors?.essential.grayLight};
      }
    }

    &.active {
      box-shadow: 0 0 5px 0 #0179c0;
      background-color: ${(props) => props.theme.colors?.essential.grayLight};
    }

    .pin-mode-button {
      appearance: none;
      background: none;
      border: none;
      outline: none;
      margin: 0;
      padding: 0;
      width: 32px;
      height: 32px;
      position: relative;
      top: -5px;
      left: -5px;
      &:hover {
        appearance: none;
        background: none;
        border: none;
        outline: none;
      }

      .pin-icon {
        height: 42px;
      }

      .plus-icon {
        height: 12px;
        position: absolute;
        bottom: -10px;
        right: -10px;
      }
    }

    @media (max-width: ${(props) =>
        props.theme.utils.getThemeBreakpoint("tablet")}) {
      right: 10px;
      top: 252px;
    }
  }
`;

const BasemapToggleContainer = styled.div<IStyledComponentProps>`
  &.basemap-toggle-container {
    width: 32px;
    height: 32px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    margin: 0;
    padding: 0;
    position: absolute;
    top: 260px;
    right: 20px;

    &.inactive {
      background-color: ${(props) => props.theme.colors?.essential.white};
      &:hover {
        background-color: ${(props) => props.theme.colors?.essential.grayLight};
      }
    }

    &.active {
      box-shadow: 0 0 5px 0 #0179c0;
      background-color: ${(props) => props.theme.colors?.essential.grayLight};
    }

    .basemap-toggle-button {
      appearance: none;
      background: none;
      border: none;
      outline: none;
      margin: 0;
      padding: 0;
      width: 32px;
      height: 32px;
      &:hover {
        appearance: none;
        background: none;
        border: none;
        outline: none;
      }

      img {
        margin-bottom: 2px;
        height: 24px;
      }
    }

    @media (max-width: ${(props) =>
        props.theme.utils.getThemeBreakpoint("tablet")}) {
      right: 10px;
      top: 296px;
    }
  }
`;

const SearchOutagesContainer = styled.div<IStyledComponentProps>`
  &.search-outages-container {
    position: absolute;
    bottom: 36px;
    left: 0;
    transition: all 300ms ease-out;
    opacity: 1;
    padding-left: 20px;
    button.lookup-button {
      color: ${(props) => props.theme.colors.brand.primary};
      border-color: ${(props) => props.theme.colors.brand.primary};
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

      &:hover {
        color: ${(props) => props.theme.colors.brand.secondary}!important;
        border-color: ${(props) =>
          props.theme.colors.brand.secondary}!important;
        cursor: pointer;
      }

      img {
        margin-right: 4px;
        width: 26px;
      }
    }

    &.hidden {
      opacity: 0;
    }

    @media (max-width: ${(props) =>
        props.theme.utils.getThemeBreakpoint("tablet")}) {
      bottom: 48px;
      text-align: initial;
      padding-right: 12px;
      padding-left: 12px;
      width: 100%;
      button.lookup-button {
        width: 100%;
      }
    }
  }
`;

const AdminFooterContainer = styled.div<IStyledComponentProps>`
  &.admin-footer-container {
    background-color: ${(props) => props.theme.colors.essential.white};
    bottom: 16px;
    margin: 0;
    position: absolute;
    padding: 0;
    right: 0;

    footer {
      text-align: center;
      font-size: ${(props) => props.theme.fontSizes.smallest}px;
      padding: 4px;
    }

    @media (max-width: ${(props) =>
        props.theme.utils.getThemeBreakpoint("tablet")}) {
      left: 0;
      right: initial;
    }
  }
`;

export default {
  AdminFooterContainer,
  BasemapToggleContainer,
  MapContainer,
  BasemapGalleryContainer,
  SearchContainer,
  LocationContainer,
  ZoomContainer,
  PinModeContainer,
  SearchOutagesContainer,
};
